/* EditReportForm.jsx */
import React, { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Breadcrumbs } from '../../../AbstractElements';
import { useLoader } from '../../../_helper/LoaderContext';
import { getUnitList, getUnitMasterList } from '../../UnitManagment/service/unit-service';
import { addParameter, updateParameter, viewParameter } from './service/parameter-service';
import { toast } from 'react-toastify';
import { Link, useParams, useNavigate } from "react-router-dom";
import Select, { components } from 'react-select';

const EditReportForm = () => {
    const { id } = useParams();
    const [formData, setFormData] = useState({
        parameterName: '',
        parameterCode: '',
        measurementType: 'quantitative',
        unit: '',
        ranges: [
            {
                gender: '',
                minAgeGroup: '',
                // maxAgeGroup: 'Adult',
                referenceText: '',
                minValue: '',
                maxValue: '',
                note: '',
            },
        ],
    });

    const [errors, setErrors] = useState({});
    const showMinMax = formData.measurementType === 'quantitative';
    const { setLoading } = useLoader()
    const [unit, setUnit] = useState([])
    const navigate = useNavigate();
    const [selectedUnit, setSelectedUnit] = useState(null);

    useEffect(() => {
        getUintMaster();
        getParameterData();
    }, [])

    const getParameterData = async () => {
        setLoading(true);
        try {
            const res = await viewParameter(id);
            if (res.data.success === true) {
                const data = res.data.data;

                const formattedData = {
                    parameterName: data.name || "",
                    parameterCode: data.code || "",
                    measurementType: data.measurementType || "quantitative",
                    unit: data.unitId || "", // keep ID if exists
                    ranges: data.referenceRanges?.length > 0
                        ? data.referenceRanges.map((r) => ({
                            gender: r.gender || "",
                            minAgeGroup: r.ageCategory || "",
                            minValue: r.minValue?.toString() || "",
                            maxValue: r.maxValue?.toString() || "",
                            note: r.note || "",
                            referenceText: r.qualitativeValue || r.ratioValue || r.scaleValue || ""
                        }))
                        : [{
                            gender: "",
                            minAgeGroup: "",
                            minValue: "",
                            maxValue: "",
                            note: "",
                            referenceText: "",
                        }],
                };

                setFormData(formattedData);

                // === UNIT HANDLING: Always show unitValue, even if not in master ===
                let unitToSelect = null;

                if (data.unitId) {
                    // Prefer matching by ID if available
                    unitToSelect = unit.find(u => u._id === data.unitId);
                }

                // If no ID match, or no ID, fall back to matching by name
                if (!unitToSelect && data.unitValue) {
                    unitToSelect = unit.find(
                        u => u.name?.trim().toLowerCase() === data.unitValue?.trim().toLowerCase()
                    );
                }

                // Final fallback: create a custom option using unitValue
                if (!unitToSelect && data.unitValue) {
                    unitToSelect = {
                        _id: data.unitId || null, // temporary ID
                        name: data.unitValue.trim()
                    };
                }

                // Only set if we have something
                if (unitToSelect) {
                    setSelectedUnit(unitToSelect);
                    // Ensure formData.unit has the correct ID (or empty if custom)
                    setFormData(prev => ({
                        ...prev,
                        unit: unitToSelect._id || ""
                    }));
                }
            }
        } catch (error) {
            console.error("Error fetching parameter:", error);
            toast.warning("Failed to load parameter data");
        } finally {
            setLoading(false);
        }
    };

    const getUintMaster = async () => {
        setLoading(true)
        try {
            const res = await getUnitMasterList()
            if (res.data.success == true) {
                setUnit(res.data.data)
            }
        }
        catch (error) { console.log(error) }
        finally {
            setLoading(false)
        }
    }

    /* ---------- Change Handlers ---------- */
    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleRangeChange = (idx, field, value) => {
        const newRanges = [...formData.ranges];
        newRanges[idx] = { ...newRanges[idx], [field]: value };
        setFormData(prev => ({ ...prev, ranges: newRanges }));
        setErrors(prev => {
            const copy = { ...prev };
            delete copy[`range_${idx}_${field}`];
            return copy;
        });
    };

    /* ---------- Add / Remove Range ---------- */
    const addRange = () => {
        setFormData(prev => ({
            ...prev,
            ranges: [
                ...prev.ranges,
                {
                    gender: '',
                    minAgeGroup: '',
                    // maxAgeGroup: 'Adult',
                    referenceText: '',
                    minValue: '',
                    maxValue: '',
                    note: '',
                },
            ],
        }));
    };

    const removeRange = idx => {
        setFormData(prev => ({
            ...prev,
            ranges: prev.ranges.filter((_, i) => i !== idx),
        }));
    };

    /* ---------- Validation ---------- */
    const validate = () => {
        const newErr = {};

        // Top-level required fields
        if (!formData.parameterName.trim())
            newErr.parameterName = 'Parameter name is required';

        // Validate each range
        formData.ranges.forEach((r, i) => {
            // Gender
            if (!r.gender) newErr[`range_${i}_gender`] = 'Gender is required';

            // Min Age Group
            if (!r.minAgeGroup) newErr[`range_${i}_minAgeGroup`] = 'age group is required';

            // // Max Age Group
            // if (!r.maxAgeGroup) newErr[`range_${i}_maxAgeGroup`] = 'Max age group is required';

            // Min / Max Value (only for quantitative)
            if (showMinMax) {
                if (!r.minValue.trim())
                    newErr[`range_${i}_minValue`] = 'Min value is required';
                if (!r.maxValue.trim())
                    newErr[`range_${i}_maxValue`] = 'Max value is required';

                // Min <= Max numeric check
                const min = parseFloat(r.minValue);
                const max = parseFloat(r.maxValue);
                if (!isNaN(min) && !isNaN(max) && min > max) {
                    newErr[`range_${i}_minValue`] = 'Min value must be ≤ Max value';
                }
            }

            // Cross-group validation: Child cannot be after Adult
            const groupOrder = { Child: 1, Adult: 2 };
            if (
                groupOrder[r.minAgeGroup] &&
                groupOrder[r.maxAgeGroup] &&
                groupOrder[r.minAgeGroup] > groupOrder[r.maxAgeGroup]
            ) {
                newErr[`range_${i}_minAgeGroup`] = 'Min age group cannot be after Max age group';
            }
        });

        setErrors(newErr);
        return Object.keys(newErr).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validate()) {
            setLoading(true)
            console.log(selectedUnit)
            try {
                const basePayload = {
                    parameterId: id,
                    name: formData.parameterName,
                    code: formData.parameterCode,
                    unitId: selectedUnit?._id,          // ID for reference
                    unitValue: selectedUnit?.name,
                    measurementType: formData.measurementType,
                    referenceRanges: []
                    // referenceRanges: formData.ranges.map((r) => ({
                    //     gender: r.gender.toLowerCase(),         // Convert to lowercase (optional)
                    //     ageCategory: r.minAgeGroup || "all",    // Convert your minAgeGroup to ageCategory
                    //     minValue: Number(r.minValue) || 0,      // Convert to number
                    //     maxValue: Number(r.maxValue) || 0,      // Convert to number
                    //     note: r.note || "",
                    // })),
                };
                const referenceRanges = formData.ranges.map(r => {
                    const common = {
                        gender: r.gender === 'all' ? 'all' : r.gender.toLowerCase(),
                        ageCategory: r.minAgeGroup || 'all',
                        note: r.note?.trim() || ""
                    };

                    if (formData.measurementType === 'quantitative') {
                        return {
                            ...common,
                            minValue: r.minValue ? Number(r.minValue) : 0,
                            maxValue: r.maxValue ? Number(r.maxValue) : 0
                        };
                    }

                    if (formData.measurementType === 'qualitative') {
                        return {
                            ...common,
                            qualitativeValue: r.referenceText?.trim() || "Not Specified"  // Use note as result text
                        };
                    }

                    if (formData.measurementType === 'ratio') {
                        return {
                            ...common,
                            ratioValue: r.referenceText?.trim() || "Not Specified"
                        };
                    }

                    if (formData.measurementType === 'scale') {
                        return {
                            ...common,
                            scaleValue: r.referenceText?.trim() || "Not Specified"
                        };
                    }
                    return common;
                });

                const payload = {
                    ...basePayload,
                    referenceRanges
                };
                const res = await updateParameter(payload)
                if (res.data.success == true) {
                    toast.success(res.data.message)
                    navigate(`${process.env.PUBLIC_URL}/parameter-list`);
                }
            } catch (error) {
                console.log(error)
            } finally {
                setLoading(false)
            }
        }
    };

    const unitMasterOptions = unit.map((u) => ({
        value: u._id,
        label: u.name,
    }));

    return (
        <>
            <Breadcrumbs mainTitle="Edit Biomarker" parent="Home" subParent="List Of Biomarker" title="Edit Biomarker" />
            <div className="container my-5">
                <div className="card shadow">
                    <div className="card-body p-4">
                        <form noValidate>

                            {/* ---------- Top Row: Name, Code, Type, Unit ---------- */}
                            <div className="row g-3 mb-4">
                                <div className="col-md-3">
                                    <label className="form-label">
                                        Biomarker Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="parameterName"
                                        value={formData.parameterName}
                                        onChange={handleChange}
                                        placeholder="e.g., Hemoglobin"
                                        className={`form-control ${errors.parameterName ? 'is-invalid' : ''}`}
                                    />
                                    {errors.parameterName && (
                                        <div className="invalid-feedback">{errors.parameterName}</div>
                                    )}
                                </div>

                                <div className="col-md-3">
                                    <label className="form-label">
                                        Biomarker Code
                                    </label>
                                    <input
                                        type="text"
                                        name="parameterCode"
                                        value={formData.parameterCode}
                                        onChange={handleChange}
                                        placeholder="e.g., HB"
                                        className={`form-control ${errors.parameterCode ? 'is-invalid' : ''}`}
                                    />
                                    {errors.parameterCode && (
                                        <div className="invalid-feedback">{errors.parameterCode}</div>
                                    )}
                                </div>

                                <div className="col-md-3">
                                    <label className="form-label">
                                        Measurement Type <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        name="measurementType"
                                        value={formData.measurementType}
                                        onChange={handleChange}
                                        className="form-select"
                                    >
                                        <option value="quantitative">Quantitative</option>
                                        <option value="qualitative">Qualitative</option>
                                        <option value="scale">Scale</option>
                                        <option value="ratio">Ratio</option>
                                    </select>
                                </div>

                                <div className="col-md-3">
                                    <label className="form-label">
                                        Unit
                                    </label>
                                    <Select
                                        options={unitMasterOptions}
                                        value={
                                            selectedUnit
                                                ? { value: selectedUnit._id, label: selectedUnit.name }
                                                : null
                                        }
                                        onChange={(selectedOption) => {
                                            if (selectedOption) {
                                                const unitObj = unit.find((u) => u._id === selectedOption.value) || {
                                                    _id: selectedOption.value,
                                                    name: selectedOption.label
                                                };
                                                setSelectedUnit(unitObj);
                                                setFormData(prev => ({ ...prev, unit: selectedOption.value }));
                                            } else {
                                                setSelectedUnit(null);
                                                setFormData(prev => ({ ...prev, unit: "" }));
                                            }
                                            setErrors(prev => ({ ...prev, unit: '' }));
                                        }}
                                        placeholder="Select Unit"
                                        isClearable={true}
                                        classNamePrefix="react-select"
                                        className={errors.unit ? "is-invalid" : ""}
                                    />
                                    {errors.unit && <div className="invalid-feedback d-block">{errors.unit}</div>}
                                    {/* <select
                                        value={selectedUnit?._id || ''}
                                        onChange={(e) => {
                                            const unitId = e.target.value;
                                            const unitObj = unit.find(u => u._id === unitId);
                                            setSelectedUnit(unitObj || null);
                                            setFormData(prev => ({ ...prev, unit: unitId })); // keep ID for validation
                                            setErrors(prev => ({ ...prev, unit: '' }));
                                        }}
                                        className={`form-select ${errors.unit ? 'is-invalid' : ''}`}
                                    >
                                        <option value="">Select Unit</option>
                                        {unit.map((data) => (
                                            <option key={data._id} value={data._id}>
                                                {data.name}
                                            </option>
                                        ))}
                                    </select> */}
                                    {errors.unit && <div className="invalid-feedback">{errors.unit}</div>}
                                </div>
                            </div>


                            <div className="row g-4">
                                {formData.ranges.map((range, idx) => (
                                    <div key={idx} className="col-12">
                                        <div className="border rounded p-4 position-relative bg-light text-black">

                                            {/* Delete Button */}
                                            {formData.ranges.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeRange(idx)}
                                                    className="btn btn-sm btn-outline-danger position-absolute top-0 end-0 mt-2 me-2"
                                                >
                                                    Remove
                                                </button>
                                            )}

                                            <h6 className="mb-3">#{idx + 1}</h6>

                                            <div className="row g-3">
                                                {/* Gender */}
                                                <div className="col-lg-3 col-md-4">
                                                    <label className="form-label">Sex <span className="text-danger">*</span></label>
                                                    <select
                                                        value={range.gender}
                                                        onChange={e => handleRangeChange(idx, 'gender', e.target.value)}
                                                        className={`form-select ${errors[`range_${idx}_gender`] ? 'is-invalid' : ''}`}
                                                    >
                                                        <option value="">Select Gender</option>
                                                        <option value="all">All</option>
                                                        <option value="male">Male</option>
                                                        <option value="female">Female</option>
                                                    </select>
                                                    {errors[`range_${idx}_gender`] && (
                                                        <div className="invalid-feedback">{errors[`range_${idx}_gender`]}</div>
                                                    )}
                                                </div>

                                                {/* Min Age Group */}
                                                <div className="col-lg-3 col-md-4">
                                                    <label className="form-label">Age <span className="text-danger">*</span></label>
                                                    <select
                                                        value={range.minAgeGroup}
                                                        onChange={e => handleRangeChange(idx, 'minAgeGroup', e.target.value)}
                                                        className={`form-select ${errors[`range_${idx}_minAgeGroup`] ? 'is-invalid' : ''}`}
                                                    >
                                                        <option value="">Select</option>
                                                        <option value="all">All</option>
                                                        <option value="child">Child</option>
                                                        <option value="adult">Adult</option>
                                                    </select>
                                                    {errors[`range_${idx}_minAgeGroup`] && (
                                                        <div className="invalid-feedback">{errors[`range_${idx}_minAgeGroup`]}</div>
                                                    )}
                                                </div>
                                                {/* === QUANTITATIVE: Min & Max === */}
                                                {formData.measurementType === 'quantitative' && (
                                                    <>
                                                        <div className="col-lg-3 col-md-4">
                                                            <label className="form-label">Min Value <span className="text-danger">*</span></label>
                                                            <input
                                                                type="text"
                                                                value={range.minValue}
                                                                onChange={e => handleRangeChange(idx, 'minValue', e.target.value)}
                                                                placeholder="e.g., 12"
                                                                className={`form-control ${errors[`range_${idx}_minValue`] ? 'is-invalid' : ''}`}
                                                            />
                                                            {errors[`range_${idx}_minValue`] && <div className="invalid-feedback">{errors[`range_${idx}_minValue`]}</div>}
                                                        </div>
                                                        <div className="col-lg-3 col-md-4">
                                                            <label className="form-label">Max Value <span className="text-danger">*</span></label>
                                                            <input
                                                                type="text"
                                                                value={range.maxValue}
                                                                onChange={e => handleRangeChange(idx, 'maxValue', e.target.value)}
                                                                placeholder="e.g., 18"
                                                                className={`form-control ${errors[`range_${idx}_maxValue`] ? 'is-invalid' : ''}`}
                                                            />
                                                            {errors[`range_${idx}_maxValue`] && <div className="invalid-feedback">{errors[`range_${idx}_maxValue`]}</div>}
                                                        </div>
                                                    </>
                                                )}

                                                {/* === SCALE & RATIO & QUALITATIVE: Text Reference === */}
                                                {['scale', 'ratio', 'qualitative'].includes(formData.measurementType) && (
                                                    <>
                                                        {/* Main Reference Value (Required) */}
                                                        <div className="col-lg-6">
                                                            <label className="form-label">
                                                                {formData.measurementType === 'scale' && 'Scale Values'}
                                                                {formData.measurementType === 'ratio' && 'Ratio Thresholds'}
                                                                {formData.measurementType === 'qualitative' && 'Possible Results'}
                                                                <span className="text-danger">*</span>
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={range.referenceText || ''}
                                                                onChange={e => handleRangeChange(idx, 'referenceText', e.target.value)}
                                                                placeholder={
                                                                    formData.measurementType === 'scale'
                                                                        ? "e.g., Negative, Trace, 1+, 2+, 3+"
                                                                        : formData.measurementType === 'ratio'
                                                                            ? "e.g., <1:40 (Negative), ≥1:160 (Positive)"
                                                                            : "e.g., Positive, Negative, Reactive, Non-reactive"
                                                                }
                                                                className={`form-control ${errors[`range_${idx}_referenceText`] ? 'is-invalid' : ''} form-control`}
                                                            />
                                                            {errors[`range_${idx}_referenceText`] && (
                                                                <div className="invalid-feedback">{errors[`range_${idx}_referenceText`]}</div>
                                                            )}
                                                            <small className="text-muted">
                                                                Separate multiple values with commas
                                                            </small>
                                                        </div>
                                                    </>
                                                )}
                                                {/* Min Value (quantitative only) */}
                                                {/* {showMinMax && ( */}
                                                {/* <div className="col-lg-3 col-md-4">
                                                    <label className="form-label">Min Value <span className="text-danger">*</span></label>
                                                    <input
                                                        type="text"
                                                        value={range.minValue}
                                                        onChange={e => handleRangeChange(idx, 'minValue', e.target.value)}
                                                        placeholder="e.g., 12"
                                                        className={`form-control ${errors[`range_${idx}_minValue`] ? 'is-invalid' : ''}`}
                                                    />
                                                    {errors[`range_${idx}_minValue`] && (
                                                        <div className="invalid-feedback">{errors[`range_${idx}_minValue`]}</div>
                                                    )}
                                                </div> */}
                                                {/* // )} */}

                                                {/* Max Value (quantitative only) */}
                                                {/* {showMinMax && ( */}
                                                {/* <div className="col-lg-3 col-md-4">
                                                    <label className="form-label">Max Value <span className="text-danger">*</span></label>
                                                    <input
                                                        type="text"
                                                        value={range.maxValue}
                                                        onChange={e => handleRangeChange(idx, 'maxValue', e.target.value)}
                                                        placeholder="e.g., 18"
                                                        className={`form-control ${errors[`range_${idx}_maxValue`] ? 'is-invalid' : ''}`}
                                                    />
                                                    {errors[`range_${idx}_maxValue`] && (
                                                        <div className="invalid-feedback">{errors[`range_${idx}_maxValue`]}</div>
                                                    )}
                                                </div> */}
                                                {/* )} */}
                                            </div>

                                            {/* Note */}
                                            <div className="mt-3">
                                                <label className="form-label">Note (Optional)</label>
                                                <input
                                                    type="text"
                                                    value={range.note}
                                                    onChange={e => handleRangeChange(idx, 'note', e.target.value)}
                                                    placeholder="e.g., Low values may indicate anemia"
                                                    className="form-control"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <div className="col-md-12 text-end">

                                    {/* ---------- Reference Ranges ---------- */}
                                    <div className="">
                                        <button
                                            type="button"
                                            onClick={addRange}
                                            className="btn btn-primary"
                                        >
                                            Add Range
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* ---------- Buttons ---------- */}
                            <div className="d-flex justify-content-end gap-2 mt-4">
                                <button onClick={handleSubmit} className="btn btn-primary">
                                    Update Biomarker
                                </button>
                                <Link to={`${process.env.PUBLIC_URL}/parameter-list`}>
                                    <button className="btn btn-secondary">
                                        Cancel
                                    </button>
                                </Link>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EditReportForm;